import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaTimes, FaUpload } from 'react-icons/fa';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import { API_BASE_URL, CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../constants';
import ButtonSpinner from '../components/ButtonSpinner';

const AddTeacher = () => {
  const navigate = useNavigate();
  const { data: dashboardData, loading, error: fetchError } = useFetch(`${API_BASE_URL}/api/web/admin/all-data`, 'allData');
  const subjects = dashboardData?.subjects || [];
  const juniorSubjects = dashboardData?.juniorSubjects || [];
  const seniorSubjects = dashboardData?.seniorSubjects || [];
  const classrooms = dashboardData?.classrooms || [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const titleOptions = [
    { value: 'Mr', label: 'Mr' },
    { value: 'Mrs', label: 'Mrs' },
    { value: 'Master', label: 'Master' },
    { value: 'Miss', label: 'Miss' },
  ];

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
  ];

  const statesInNigeria = [
    { value: 'Abia', label: 'Abia' },
    { value: 'Adamawa', label: 'Adamawa' },
    { value: 'Akwa Ibom', label: 'Akwa Ibom' },
    { value: 'Anambra', label: 'Anambra' },
    { value: 'Bauchi', label: 'Bauchi' },
    { value: 'Bayelsa', label: 'Bayelsa' },
    { value: 'Benue', label: 'Benue' },
    { value: 'Borno', label: 'Borno' },
    { value: 'Cross River', label: 'Cross River' },
    { value: 'Delta', label: 'Delta' },
    { value: 'Ebonyi', label: 'Ebonyi' },
    { value: 'Edo', label: 'Edo' },
    { value: 'Ekiti', label: 'Ekiti' },
    { value: 'Enugu', label: 'Enugu' },
    { value: 'Gombe', label: 'Gombe' },
    { value: 'Imo', label: 'Imo' },
    { value: 'Jigawa', label: 'Jigawa' },
    { value: 'Kaduna', label: 'Kaduna' },
    { value: 'Kano', label: 'Kano' },
    { value: 'Katsina', label: 'Katsina' },
    { value: 'Kebbi', label: 'Kebbi' },
    { value: 'Kogi', label: 'Kogi' },
    { value: 'Kwara', label: 'Kwara' },
    { value: 'Lagos', label: 'Lagos' },
    { value: 'Nasarawa', label: 'Nasarawa' },
    { value: 'Niger', label: 'Niger' },
    { value: 'Ogun', label: 'Ogun' },
    { value: 'Ondo', label: 'Ondo' },
    { value: 'Osun', label: 'Osun' },
    { value: 'Oyo', label: 'Oyo' },
    { value: 'Plateau', label: 'Plateau' },
    { value: 'Rivers', label: 'Rivers' },
    { value: 'Sokoto', label: 'Sokoto' },
    { value: 'Taraba', label: 'Taraba' },
    { value: 'Yobe', label: 'Yobe' },
    { value: 'Zamfara', label: 'Zamfara' },
    { value: 'FCT', label: 'FCT' },
  ];

  const [formData, setFormData] = useState({
    title: null,
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    alternative_phone: '',
    emergency_phone: '',
    gender: null,
    employment_date: '',
    state_of_origin: null,
    lga: '',
    current_address: '',
    subjects: null,
    classes: [],
    qualification: '',
    experience: '',
    profilePicture: null,
    status: 'pending',
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  const subjectOptions = subjects.map((s) => ({ value: s.subject_name, label: s.subject_name }));
  const classOptions = classrooms.map((c) => ({ value: c.class_name, label: c.class_name }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('File size should not exceed 2MB.');
        return;
      }
      setError('');
      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate required fields
    const requiredFields = ['title', 'firstName', 'lastName', 'email', 'phone', 'gender', 'qualification', 'experience'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`Please fill in the ${field.replace('_', ' ')} field.`);
        setIsSubmitting(false);
        return;
      }
    }

    // Upload to Cloudinary
    let imageUrl = null;
    if (formData.profilePicture) {
      const cloudData = new FormData();
      cloudData.append('file', formData.profilePicture);
      cloudData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      cloudData.append('folder', 'profiles/teachers');

      try {
        const cloudRes = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
          { method: 'POST', body: cloudData }
        );
        const json = await cloudRes.json();
        if (!json.secure_url) {
          throw new Error('Cloudinary upload failed');
        }
        imageUrl = json.secure_url;
      } catch (err) {
        console.error('Cloudinary upload failed:', err);
        setError('Failed to upload profile picture.');
        setIsSubmitting(false);
        return;
      }
    }

    // Build FormData payload
    const payload = new FormData();
    const textFields = [
      'title', 'firstName', 'middleName', 'lastName', 'email', 'password',
      'phone', 'alternative_phone', 'emergency_phone', 'gender',
      'employment_date', 'state_of_origin', 'lga', 'current_address',
      'qualification', 'experience', 'status'
    ];
    textFields.forEach((name) => {
      if (formData[name]) {
        payload.append(name, formData[name]);
      }
    });

    if (imageUrl) {
      payload.append('profilePicture', imageUrl);
    }
    if (formData.profilePicture) {
      payload.append('local_profile', formData.profilePicture);
    }

    const subjectValues = formData.subjects ? [formData.subjects.value] : [];
    payload.append('subjects', JSON.stringify(subjectValues));
    const classValues = formData.classes?.length ? formData.classes.map((c) => c.value) : [];
    payload.append('classes', JSON.stringify(classValues));

    // Submit to API
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/admin/add-teacher`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: payload,
      });
      const result = await res.json();
      if (result.success) {
        alert('Teacher added successfully!');
        navigate('/teachers');
      } else {
        setError(result.message || 'Failed to add teacher.');
      }
    } catch (err) {
      console.error('Error creating teacher:', err);
      setError('Error creating teacher. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Add New Teacher</h1>
            <p className="text-blue-100">Fill in the details to add a new teacher</p>
          </div>
          <Link
            to="/teachers"
            className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5" />
            <span>Back to Teachers</span>
          </Link>
        </div>
      </div>

      {/* Form Container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        {fetchError && <div className="text-red-600 text-center mb-4">{fetchError}</div>}
        {error && <div className="text-red-600 text-center mb-4">{error}</div>}
        {loading && <div className="text-gray-600 dark:text-gray-400 text-center mb-4">Loading subjects and classrooms...</div>}
        <form onSubmit={handleSubmit}>
          {/* Profile Picture Section */}
          <div className="mb-8 flex flex-col items-center">
            <div className="relative w-32 h-32 mb-4">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Profile preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FaUpload className="text-gray-400 text-3xl" />
                )}
              </div>
              <label
                htmlFor="profilePicture"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
              >
                <FaUpload className="text-sm" />
              </label>
              <input
                type="file"
                id="profilePicture"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePictureChange}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Upload profile picture (max 2MB)</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
              <Select
                options={titleOptions}
                value={titleOptions.find((option) => option.value === formData.title)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, title: selected?.value }))}
                className="text-gray-900 dark:text-white"
                classNamePrefix="select"
                placeholder="Select title"
                isDisabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Middle Name</label>
              <input
                type="text"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Alternative Phone Number</label>
              <input
                type="tel"
                name="alternative_phone"
                value={formData.alternative_phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Emergency Phone Number</label>
              <input
                type="tel"
                name="emergency_phone"
                value={formData.emergency_phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gender *</label>
              <Select
                options={genderOptions}
                value={genderOptions.find((option) => option.value === formData.gender)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, gender: selected?.value }))}
                className="text-gray-900 dark:text-white"
                classNamePrefix="select"
                placeholder="Select gender"
                isDisabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject</label>
              <Select
                options={subjectOptions}
                value={formData.subjects}
                onChange={(selected) => setFormData((prev) => ({ ...prev, subjects: selected }))}
                className="text-gray-900 dark:text-white"
                classNamePrefix="select"
                placeholder="Select subject"
                isDisabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Employment Date</label>
              <input
                type="date"
                name="employment_date"
                value={formData.employment_date}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Classes</label>
              <Select
                isMulti
                options={classOptions}
                value={formData.classes}
                onChange={(selected) => setFormData((prev) => ({ ...prev, classes: selected || [] }))}
                className="text-gray-900 dark:text-white"
                classNamePrefix="select"
                placeholder="Select classes"
                isDisabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">State of Origin</label>
              <Select
                options={statesInNigeria}
                value={statesInNigeria.find((option) => option.value === formData.state_of_origin)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, state_of_origin: selected?.value }))}
                className="text-gray-900 dark:text-white"
                classNamePrefix="select"
                placeholder="Select state"
                isDisabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">LGA</label>
              <input
                type="text"
                name="lga"
                value={formData.lga}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Address</label>
              <input
                type="text"
                name="current_address"
                value={formData.current_address}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Qualifications *</label>
              <textarea
                name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-h-[120px]"
                required
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teaching Experience *</label>
              <textarea
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-h-[120px]"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status *</label>
              <Select
                options={statusOptions}
                value={statusOptions.find((option) => option.value === formData.status)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, status: selected?.value }))}
                className="text-gray-900 dark:text-white"
                classNamePrefix="select"
                placeholder="Select status"
                isDisabled={loading}
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <Link
              to="/teachers"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FaTimes />
              Cancel
            </Link>
            <button
              disabled={isSubmitting || loading}
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <ButtonSpinner size="small" color="white" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave />
                  Create Teacher
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTeacher;